import { Link } from "@tanstack/react-router";
import { useRef } from "react";
import { DEFAULT_CATEGORIES, type Category, type Item } from "@instead/shared";
import { useT, type T } from "../lib/i18n.ts";
import { Cover } from "./Cover.tsx";
import { Ic } from "./Icons.tsx";
import { fmtDuration } from "./Ui.tsx";

/** A built-in still carrying its seed name is shown translated; a renamed one shows the name. */
export const catLabel = (c: Category | undefined, t: T): string =>
  !c ? "" : c.kind === "custom" || DEFAULT_CATEGORIES.find((d) => d.kind === c.kind)?.name !== c.name ? c.name : t(`cat.${c.kind}` as never);

/** "12 min · activity · In progress" */
export function metaLine(item: Item, cats: Category[], t: T): React.ReactNode {
  const cat = cats.find((c) => c.id === item.categoryId);
  const parts: React.ReactNode[] = [];
  const d = fmtDuration(item.durationMin, item.durationMaxMin);
  if (d) parts.push(d);
  if (cat) parts.push(catLabel(cat, t).toLowerCase());
  if (item.tracksProgress && item.status !== "unstarted") {
    parts.push(<span key="p" className="progress-word">{t(`item.status.${item.status}` as never)}</span>);
  }
  return parts.map((p, i) => <span key={i}>{i > 0 ? " · " : ""}{p}</span>);
}

/**
 * One dealt card. No Start button (D-25). View mode: the whole card opens the
 * item; the TITLE goes straight to the content when there is a link (founder
 * feedback 2026-09-02). Edit mode: a drag handle (pointer drag, arrow keys),
 * redraw / replace from the shelf / remove — no visible up/down buttons.
 */
export function DealCard({ item, cats, mode = "view", onOpen, onRedraw, onReplace, onRemove, onMove, handleProps, cardProps, showNote = true }: {
  item: Item;
  cats: Category[];
  mode?: "view" | "edit";
  onOpen?: (item: Item) => void;
  onRedraw?: () => void;
  onReplace?: () => void;
  onRemove?: () => void;
  onMove?: (dir: -1 | 1) => void;
  handleProps?: React.HTMLAttributes<HTMLElement>;
  cardProps?: Record<string, string>;
  showNote?: boolean;
}) {
  const t = useT();
  const title = mode === "view" && item.link ? (
    <a className="title-link" href={item.link} target="_blank" rel="noopener noreferrer" onClick={() => onOpen?.(item)}>
      {item.title} <span style={{ display: "inline-flex", verticalAlign: "-1px" }}>{Ic.external({ size: 13 })}</span>
    </a>
  ) : item.title;
  return (
    <article className={`card deal-card ${mode === "view" ? "deal-card-view" : ""}`} {...(mode === "edit" ? cardProps : {})}>
      {mode === "view" && (
        // stretched over the card (CSS); sits under the title link
        <Link className="card-hit" to="/item/$id" params={{ id: item.id }} aria-label={`${item.title} — ${t("deal.openCard")}`} onClick={() => { if (!item.link) onOpen?.(item); }} />
      )}
      {mode === "edit" && (
        <button type="button" className="icon-btn drag-handle" aria-label={t("deal.reorder")} title={t("deal.reorderHint")}
          {...handleProps}
          onKeyDown={(e) => {
            if (!onMove) return;
            if (e.key === "ArrowUp") { e.preventDefault(); onMove(-1); }
            if (e.key === "ArrowDown") { e.preventDefault(); onMove(1); }
          }}>
          {Ic.drag({ size: 16 })}
        </button>
      )}
      <Cover item={item} size="s" />
      <div className="deal-body">
        <h2 className="title">{title}</h2>
        <p className="meta">{metaLine(item, cats, t)}</p>
        {showNote && mode === "view" && item.annotation && <p className="note">{item.annotation}</p>}
      </div>
      {mode === "edit" && (
        <span className="deal-actions">
          {onRedraw && <button type="button" className="icon-btn" aria-label={t("deal.redraw")} title={t("deal.redraw")} onClick={onRedraw}>{Ic.refresh({ size: 14 })}</button>}
          {onReplace && <button type="button" className="icon-btn" aria-label={t("deal.replace")} title={t("deal.replace")} onClick={onReplace}>{Ic.shelf({ size: 14 })}</button>}
          {onRemove && <button type="button" className="icon-btn danger" aria-label={t("deal.removeCard")} title={t("deal.removeCard")} onClick={onRemove}>{Ic.x({ size: 14 })}</button>}
        </span>
      )}
    </article>
  );
}

/** Row geometry captured once, at pointer-down, in document coordinates (auto-scroll moves the viewport under the finger). */
type RowBox = { id: string; el: HTMLElement; top: number; height: number };
type Drag = { id: string; from: number; to: number; rows: RowBox[]; slot: number; startY: number; lastClientY: number; raf: number };

/** Distance from the viewport edge inside which a drag scrolls the page, and the fastest it goes (px per frame). */
const SCROLL_EDGE = 56;
const SCROLL_MAX = 14;

/** `--dur-2` as a number of ms — 0 under prefers-reduced-motion / data-motion=reduced, so the settle is instant. */
const durMs = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--dur-2")) || 0;

/**
 * Reorder a list of ids by dragging a handle (pointer events — HTML5 drag
 * just scrolled on touch) or with the arrow keys on it. The held row lifts
 * as a card and follows the pointer; neighbours slide out of its way; the DOM
 * order changes only on release (feedback F5). `cardProps` marks the row the
 * handle belongs to; `handleProps(id, name)` labels the handle with the item's
 * name and its "n of m" position; `onDrop` fires once with the final order.
 */
export function useReorder(ids: string[], setIds: (next: string[]) => void, onDrop?: (next: string[]) => void) {
  const t = useT();
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const drag = useRef<Drag | null>(null);

  const move = (id: string, dir: -1 | 1) => {
    const cur = idsRef.current;
    const i = cur.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= cur.length) return;
    const next = [...cur];
    [next[i], next[j]] = [next[j]!, next[i]!];
    idsRef.current = next;
    setIds(next);
    onDrop?.(next);
  };

  /** Move the held row under the pointer and open the slot it would land in (thresholds are the rows' original midpoints — no jitter). */
  const layout = (d: Drag, clientY: number) => {
    d.lastClientY = clientY;
    const y = clientY + window.scrollY - d.startY;
    const held = d.rows[d.from]!;
    const centre = held.top + held.height / 2 + y;
    let to = d.from;
    if (y > 0) { for (let i = d.from + 1; i < d.rows.length; i++) if (centre > d.rows[i]!.top + d.rows[i]!.height / 2) to = i; }
    else { for (let i = d.from - 1; i >= 0; i--) if (centre < d.rows[i]!.top + d.rows[i]!.height / 2) to = i; }
    d.to = to;
    held.el.style.setProperty("--drag-y", `${y}px`);
    d.rows.forEach((r, i) => {
      if (i === d.from) return;
      const shift = to > d.from && i > d.from && i <= to ? -d.slot : to < d.from && i >= to && i < d.from ? d.slot : 0;
      r.el.style.transform = shift ? `translateY(${shift}px)` : "";
    });
  };

  /** Scroll the page while the pointer sits near the top or bottom edge; the held row keeps up. */
  const tick = () => {
    const d = drag.current;
    if (!d) return;
    const y = d.lastClientY;
    const h = window.innerHeight;
    const speed = y < SCROLL_EDGE ? -Math.ceil(((SCROLL_EDGE - y) / SCROLL_EDGE) * SCROLL_MAX) : y > h - SCROLL_EDGE ? Math.ceil(((y - (h - SCROLL_EDGE)) / SCROLL_EDGE) * SCROLL_MAX) : 0;
    if (speed) {
      const before = window.scrollY;
      window.scrollBy(0, speed);
      if (window.scrollY !== before) layout(d, y);
    }
    d.raf = requestAnimationFrame(tick);
  };

  /** Transition and transform go in the same style flush, so nothing animates back while the DOM reorders. */
  const clear = (d: Drag) => {
    for (const r of d.rows) { r.el.style.transition = ""; r.el.style.transform = ""; r.el.style.removeProperty("--drag-y"); r.el.classList.remove("dragging", "dropping"); }
  };

  const end = (e: React.PointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    cancelAnimationFrame(d.raf);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    const next = [...idsRef.current];
    next.splice(d.from, 1);
    next.splice(d.to, 0, d.id);
    const finish = () => {
      clear(d);
      idsRef.current = next;
      setIds(next);
      onDrop?.(next);
    };
    const ms = durMs();
    if (ms === 0 || e.type === "pointercancel") { finish(); return; }
    // settle onto the open slot, then swap the DOM order in the same frame the transforms are dropped
    const held = d.rows[d.from]!;
    const target = d.rows[d.to]!;
    const landing = d.to > d.from ? target.top + target.height - held.height - held.top : target.top - held.top;
    held.el.classList.replace("dragging", "dropping");
    held.el.style.setProperty("--drag-y", `${landing}px`);
    let done = false;
    const once = () => { if (done) return; done = true; held.el.removeEventListener("transitionend", once); finish(); };
    held.el.addEventListener("transitionend", once);
    window.setTimeout(once, ms + 50);
  };

  const handleProps = (id: string, name: string): React.HTMLAttributes<HTMLElement> => ({
    "aria-label": t("deal.reorderPos", { name, n: ids.indexOf(id) + 1, m: ids.length }),
    onPointerDown: (e) => {
      if (drag.current) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      const rows: RowBox[] = [];
      for (const rid of idsRef.current) {
        const el = document.querySelector<HTMLElement>(`[data-reorder-id="${CSS.escape(rid)}"]`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        rows.push({ id: rid, el, top: r.top + window.scrollY, height: r.height });
      }
      const from = rows.findIndex((r) => r.id === id);
      if (from < 0) return;
      const held = rows[from]!;
      const prev = rows[from - 1];
      const after = rows[from + 1];
      const gap = prev ? held.top - (prev.top + prev.height) : after ? after.top - (held.top + held.height) : 0;
      const d: Drag = { id, from, to: from, rows, slot: held.height + gap, startY: e.clientY + window.scrollY, lastClientY: e.clientY, raf: 0 };
      drag.current = d;
      e.currentTarget.setPointerCapture(e.pointerId);
      for (const r of rows) if (r !== held) r.el.style.transition = "transform var(--dur-2) var(--ease)";
      held.el.classList.add("dragging");
      layout(d, e.clientY);
      d.raf = requestAnimationFrame(tick);
    },
    onPointerMove: (e) => {
      const d = drag.current;
      if (!d || d.id !== id) return;
      e.preventDefault();
      layout(d, e.clientY);
    },
    onPointerUp: end,
    onPointerCancel: end,
  });
  const cardProps = (id: string): Record<string, string> => ({ "data-reorder-id": id });
  return { move, handleProps, cardProps };
}
